// ---------------------------------------------------------------------------
// Campaign fallback data layer (OPUS-D14)
// ---------------------------------------------------------------------------
// Thin bindings + TanStack Query hooks for the `/api/v1/campaign-fallback`
// router declared in `apps/api/src/routes/campaign-fallback.ts` (feature #76).
//
// Endpoints mirrored here:
//   GET    /:campaignId           list every reason row for a campaign
//   GET    /:campaignId?reason=x  single-row read for a specific reason
//   POST   /                      create { campaignId, fallbackUrl, reason }
//   PUT    /:campaignId           update existing { fallbackUrl, reason? }
//   DELETE /:campaignId?reason=x  remove a single row
//
// "enabled toggle" semantics at the UI layer: when the user flips the toggle
// off we DELETE the row; toggling on re-POSTs (or PUTs when a row already
// exists). This keeps the API contract untouched — the server has no enabled
// bit, it simply treats row absence as disabled.
//
// No emojis. No competitor names. Light theme copy only.
// ---------------------------------------------------------------------------

import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";

import { ApiError, apiClient } from "../../api-client";

// ---------------------------------------------------------------------------
// Domain types — shape-matched to the api router's JSON responses.
// ---------------------------------------------------------------------------

export const FALLBACK_REASONS = ["geo", "device", "os", "any"] as const;
export type FallbackReason = (typeof FALLBACK_REASONS)[number];

export const REASON_LABEL: Record<FallbackReason, string> = {
	any: "Any blocked click",
	geo: "Geo blocked",
	device: "Device blocked",
	os: "OS blocked",
};

export interface CampaignFallbackView {
	id: string;
	campaign_id: string;
	reason: FallbackReason;
	fallback_url: string;
	created_at: number;
	updated_at: number;
}

export interface FallbackListResponse {
	campaign_id: string;
	fallbacks: CampaignFallbackView[];
}

export interface FallbackSingleResponse {
	fallback: CampaignFallbackView;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const BASE_PATH = "/campaign-fallback";

// ---------------------------------------------------------------------------
// Zod-style validators (hand-rolled — react-hook-form / zod are not in
// the dash lockfile; package.json edits are out of agent scope).
// ---------------------------------------------------------------------------

export interface FallbackFormInput {
	campaignId: string;
	fallbackUrl: string;
	reason: FallbackReason;
	enabled: boolean;
}

export interface FallbackFormErrors {
	campaignId?: string;
	fallbackUrl?: string;
	reason?: string;
}

const CAMPAIGN_ID_RE = /^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/;

/**
 * Emits a lightweight Zod-schema-like error map without pulling the zod
 * package. Mirrors the api-side `validateUrl` behaviour so client errors
 * match server errors one-for-one.
 */
export function validateFallbackInput(
	input: FallbackFormInput,
): FallbackFormErrors {
	const errors: FallbackFormErrors = {};
	const id = input.campaignId.trim();
	if (!id) {
		errors.campaignId = "Campaign ID is required.";
	} else if (!CAMPAIGN_ID_RE.test(id)) {
		errors.campaignId =
			"Use letters, digits, hyphens, or underscores. 1–128 chars.";
	}

	if (input.enabled) {
		const url = input.fallbackUrl.trim();
		if (!url) {
			errors.fallbackUrl = "Fallback URL is required when enabled.";
		} else if (url.length > 2048) {
			errors.fallbackUrl = "Keep the URL under 2048 characters.";
		} else {
			let parsed: URL | null = null;
			try {
				parsed = new URL(url);
			} catch {
				parsed = null;
			}
			if (!parsed) {
				errors.fallbackUrl = "Enter a valid absolute URL.";
			} else if (parsed.protocol !== "https:") {
				errors.fallbackUrl = "URL must start with https://.";
			} else if (!parsed.hostname) {
				errors.fallbackUrl = "URL must include a host.";
			}
		}
	}

	if (!(FALLBACK_REASONS as readonly string[]).includes(input.reason)) {
		errors.reason = "Pick a valid failure reason.";
	}

	return errors;
}

// ---------------------------------------------------------------------------
// Fetchers — apiClient already injects x-tenant-id + base url.
// ---------------------------------------------------------------------------

export async function listFallbacks(
	campaignId: string,
): Promise<CampaignFallbackView[]> {
	const res = await apiClient.get<FallbackListResponse>(
		`${BASE_PATH}/${encodeURIComponent(campaignId)}`,
	);
	return Array.isArray(res.fallbacks) ? res.fallbacks : [];
}

export async function getFallback(
	campaignId: string,
	reason: FallbackReason,
): Promise<CampaignFallbackView | null> {
	try {
		const res = await apiClient.get<FallbackSingleResponse>(
			`${BASE_PATH}/${encodeURIComponent(campaignId)}`,
			{ query: { reason } },
		);
		return res.fallback ?? null;
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return null;
		throw err;
	}
}

export interface CreateFallbackInput {
	campaignId: string;
	fallbackUrl: string;
	reason: FallbackReason;
}

export async function createFallback(
	input: CreateFallbackInput,
): Promise<CampaignFallbackView> {
	const res = await apiClient.post<FallbackSingleResponse>(BASE_PATH, {
		campaignId: input.campaignId,
		fallbackUrl: input.fallbackUrl,
		reason: input.reason,
	});
	return res.fallback;
}

export interface UpdateFallbackInput {
	campaignId: string;
	fallbackUrl: string;
	reason: FallbackReason;
}

export async function updateFallback(
	input: UpdateFallbackInput,
): Promise<CampaignFallbackView> {
	const res = await apiClient.put<FallbackSingleResponse>(
		`${BASE_PATH}/${encodeURIComponent(input.campaignId)}`,
		{ fallbackUrl: input.fallbackUrl, reason: input.reason },
		{ query: { reason: input.reason } },
	);
	return res.fallback;
}

export async function deleteFallback(
	campaignId: string,
	reason: FallbackReason,
): Promise<void> {
	await apiClient.delete(
		`${BASE_PATH}/${encodeURIComponent(campaignId)}`,
		{ query: { reason } },
	);
}

/**
 * Idempotent save used by the form "Save" button. If a row with that reason
 * already exists we PUT, otherwise we POST. Callers should not care which
 * verb fired — they just want the row persisted.
 */
export async function upsertFallback(
	input: CreateFallbackInput,
): Promise<CampaignFallbackView> {
	try {
		return await createFallback(input);
	} catch (err) {
		if (err instanceof ApiError && err.status === 409) {
			return await updateFallback(input);
		}
		throw err;
	}
}

// ---------------------------------------------------------------------------
// React Query integration
// ---------------------------------------------------------------------------

export const fallbackQueryKeys = {
	all: ["campaign-fallback"] as const,
	list: (campaignId: string) =>
		["campaign-fallback", "list", campaignId] as const,
	detail: (campaignId: string, reason: FallbackReason) =>
		["campaign-fallback", "detail", campaignId, reason] as const,
};

export function useFallbackList(
	campaignId: string | undefined,
): UseQueryResult<CampaignFallbackView[], Error> {
	return useQuery<CampaignFallbackView[], Error>({
		queryKey: fallbackQueryKeys.list(campaignId ?? ""),
		queryFn: () => listFallbacks(campaignId ?? ""),
		enabled: typeof campaignId === "string" && campaignId.length > 0,
		staleTime: 10_000,
	});
}

export interface SaveFallbackArgs {
	campaignId: string;
	fallbackUrl: string;
	reason: FallbackReason;
	/** When false the mutation deletes the row instead of upserting. */
	enabled: boolean;
}

/**
 * Single mutation that encapsulates the enable/disable flow.
 *   enabled=true  -> upsert (POST, fall back to PUT on 409)
 *   enabled=false -> delete (idempotent; 404 is treated as success)
 */
export function useSaveFallback(): UseMutationResult<
	CampaignFallbackView | null,
	Error,
	SaveFallbackArgs
> {
	const qc = useQueryClient();
	return useMutation<CampaignFallbackView | null, Error, SaveFallbackArgs>({
		mutationFn: async (args) => {
			if (!args.enabled) {
				try {
					await deleteFallback(args.campaignId, args.reason);
				} catch (err) {
					if (err instanceof ApiError && err.status === 404) {
						// already absent — treat as a successful disable
						return null;
					}
					throw err;
				}
				return null;
			}
			return upsertFallback({
				campaignId: args.campaignId,
				fallbackUrl: args.fallbackUrl,
				reason: args.reason,
			});
		},
		onSuccess: (_row, vars) => {
			void qc.invalidateQueries({
				queryKey: fallbackQueryKeys.list(vars.campaignId),
			});
			void qc.invalidateQueries({
				queryKey: fallbackQueryKeys.detail(vars.campaignId, vars.reason),
			});
		},
	});
}

// ---------------------------------------------------------------------------
// Error message extraction — keeps the form / toasts terse.
// ---------------------------------------------------------------------------

export function extractErrorMessage(err: unknown): string {
	if (err instanceof ApiError) {
		return err.message;
	}
	if (err instanceof Error) return err.message;
	return "Unable to save fallback.";
}
